import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TaskSummary = ({ tasks = [], userRole, loading = false, counts = null }) => {
  const navigate = useNavigate();
  const normalizedRole = userRole?.toLowerCase();

  // Status color mapping
  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    const statusMap = {
      'to do': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      'to-do': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      'todo': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      'to_do': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      'in progress': 'bg-primary-light text-primary',
      'in_progress': 'bg-primary-light text-primary',
      'in-progress': 'bg-primary-light text-primary',
      'review': 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'testing': 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'done': 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
      'completed': 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
      'complete': 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
      'blocked': 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
      'on hold': 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
      'on_hold': 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
    };
    return statusMap[s] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
  };

  // Format status for display
  const formatStatus = (status) => {
    const s = (status || '').toLowerCase();
    const displayMap = {
      'todo': 'To Do',
      'to_do': 'To Do',
      'to do': 'To Do',
      'in_progress': 'In Progress',
      'in progress': 'In Progress',
      'in-progress': 'In Progress',
      'done': 'Done',
      'completed': 'Completed',
      'complete': 'Completed',
      'review': 'Review',
      'testing': 'Testing',
      'blocked': 'Blocked',
      'on_hold': 'On Hold',
      'on hold': 'On Hold'
    };
    return displayMap[s] || status;
  };

  // Priority icon and color mapping
  const getPriorityConfig = (priority) => {
    const config = {
      'urgent': { icon: 'AlertCircle', color: 'text-red-500' },
      'high': { icon: 'AlertTriangle', color: 'text-orange-500' },
      'medium': { icon: 'Minus', color: 'text-amber-500' },
      'low': { icon: 'ArrowDown', color: 'text-green-500' }
    };
    return config[(priority || '').toLowerCase()] || { icon: 'Minus', color: 'text-slate-400' };
  };

  // Check if a task is completed
  const isCompleted = (status) => {
    const s = (status || '').toLowerCase();
    return s === 'done' || s === 'completed' || s === 'complete';
  };

  // Check if a task is overdue (only if it has a due date and is not completed)
  const isTaskOverdue = (task) => {
    if (!task.dueDate) return false;
    if (isCompleted(task.status)) return false;
    
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today;
  };

  // Calculate derived stats from tasks
  const derivedStats = useMemo(() => ({
    total: tasks.length,
    todo: tasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      return s === 'todo' || s === 'to do' || s === 'to_do';
    }).length,
    completed: tasks.filter(t => isCompleted(t.status)).length,
    inProgress: tasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      return s === 'in progress' || s === 'in_progress';
    }).length,
    review: tasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      return s === 'review' || s === 'testing' || s === 'qa';
    }).length,
    overdue: tasks.filter(t => isTaskOverdue(t)).length
  }), [tasks, isTaskOverdue]);

  // Use provided counts or fall back to derived stats
  const taskStats = useMemo(() => ({
    total: counts?.total ?? counts?.total_tasks ?? derivedStats.total,
    todo: counts?.todo ?? counts?.todo_tasks ?? derivedStats.todo,
    completed: counts?.completed ?? counts?.completed_tasks ?? derivedStats.completed,
    inProgress: counts?.inProgress ?? counts?.in_progress ?? counts?.in_progress_tasks ?? derivedStats.inProgress,
    review: counts?.review ?? counts?.review_tasks ?? derivedStats.review,
    overdue: counts?.overdue ?? counts?.overdue_tasks ?? derivedStats.overdue
  }), [counts, derivedStats]);

  // Sort tasks: overdue first, then by due date, then by updated date
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      // Overdue tasks first
      const aOverdue = isTaskOverdue(a);
      const bOverdue = isTaskOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      
      // Then by due date (nearest first)
      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      
      // Finally by updated date (most recent first)
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    });
  }, [tasks, isTaskOverdue]);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  // Navigation handlers
  const handleTaskClick = (task) => {
    if (task?.projectId) {
      navigate(`/project-management?id=${task.projectId}&tab=tasks`);
    } else {
      navigate('/my-tasks');
    }
  };

  const handleViewAllClick = () => navigate('/my-tasks');

  // Stat card component
  const StatCard = ({ value, label, colorClass, bgClass }) => (
    <div className={`group text-center p-4 ${bgClass} rounded-xl hover:shadow-md transition-all duration-300`}>
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
      <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-1">
        {label}
      </div>
    </div>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl animate-pulse">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3 mb-2" />
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  // Empty state
  const EmptyState = () => (
    <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
      <div className="w-14 h-14 bg-white dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
        <Icon name="CheckCircle2" size={28} className="text-emerald-400" />
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
        No tasks assigned to you
      </p>
      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
        Tasks will appear here when assigned
      </p>
    </div>
  );

  // Task item component
  const TaskItem = ({ task }) => {
    const overdue = isTaskOverdue(task);
    const priorityConfig = getPriorityConfig(task.priority);
    
    return (
      <div 
        onClick={() => handleTaskClick(task)}
        className={`group flex items-center gap-3 p-3 bg-white dark:bg-slate-800 border rounded-xl cursor-pointer transition-all duration-200
          ${overdue 
            ? 'border-rose-200 dark:border-rose-900/50 hover:border-rose-300 shadow-sm shadow-rose-100 dark:shadow-rose-900/10' 
            : 'border-slate-100 dark:border-slate-700 hover:border-primary/30'
          } hover:shadow-md`}
      >
        {/* Priority Icon */}
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 flex-shrink-0">
          <Icon name={priorityConfig.icon} size={16} className={priorityConfig.color} />
        </div>
        
        {/* Task Content */}
        <div className="flex-1 min-w-0">
          {/* Title and Status */}
          <div className="flex items-center gap-2 mb-1">
            <h5 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate group-hover:text-primary transition-colors">
              {task.title}
            </h5>
            <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
              {overdue && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400">
                  Overdue
                </span>
              )}
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${getStatusColor(task.status)}`}>
                {formatStatus(task.status)}
              </span>
            </div>
          </div>
          
          {/* Meta Info */}
          <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
            {/* Due Date */}
            <div className={`flex items-center gap-1 ${overdue ? 'text-rose-500 dark:text-rose-400' : ''}`}>
              <Icon name="Calendar" size={11} className={overdue ? 'text-rose-400' : 'text-slate-400'} />
              <span>{formatDisplayDate(task.dueDate)}</span>
            </div>
            
            {/* Project */}
            {task.project && (
              <div className="flex items-center gap-1 truncate max-w-[120px]">
                <Icon name="Folder" size={11} className="text-slate-400 flex-shrink-0" />
                <span className="truncate">{task.project}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg border border-border dark:border-slate-800 p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Task Summary
        </h3>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard 
          value={taskStats.total} 
          label="Total Tasks" 
          colorClass="text-slate-700 dark:text-slate-200"
          bgClass="bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700"
        />
        <StatCard 
          value={taskStats.inProgress} 
          label="In Progress" 
          colorClass="text-primary"
          bgClass="bg-primary-light border border-primary/20"
        />
        <StatCard 
          value={taskStats.completed} 
          label="Completed" 
          colorClass="text-emerald-600 dark:text-emerald-400"
          bgClass="bg-emerald-50/50 dark:bg-emerald-900/20 border border-emerald-100/50 dark:border-emerald-800/50"
        />
        <StatCard 
          value={taskStats.overdue} 
          label="Overdue" 
          colorClass="text-rose-600 dark:text-rose-400"
          bgClass="bg-rose-50/50 dark:bg-rose-900/20 border border-rose-100/50 dark:border-rose-800/50"
        />
      </div>

      {/* My Tasks Section */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
            <Icon name="ListTodo" size={16} className="text-primary" />
            My Tasks
          </h4>
          {tasks.length > 0 && (
            <span className="text-xs text-slate-400 dark:text-slate-500">
              Showing {Math.min(5, tasks.length)} of {tasks.length}
            </span>
          )}
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : tasks.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex-1 overflow-y-auto pr-1 space-y-2">
            {sortedTasks.slice(0, 5).map(task => (
              <TaskItem key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>

      {/* View All Button */}
      {tasks.length > 5 && (
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
          <Button 
            variant="ghost" 
            className="w-full text-primary hover:text-primary-hover hover:bg-primary-light" 
            iconName="ArrowRight" 
            iconPosition="right"
            onClick={handleViewAllClick}
          >
            View All My Tasks ({tasks.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default TaskSummary;